import * as React from 'react';
import Modal from 'antd/lib/modal';
import { useSelector } from 'react-redux';
import { dayjs } from '@/extendedDayjs';
import { RootState } from '../../reducers/reducers';
import { selectLifecycleFreezes } from '../../selectors/selectors';
import { findLifecycleFreezeForDate } from '../../helpers';
import { LifecycleEventType, LifecycleFreezeSet } from '../../types';

const warningForEvent = (label: string) => `You are setting a ${label} target date during a lifecycle freeze.`;
const CONFIRM_TITLE = 'Set target date during a lifecycle freeze?';
const CONFIRM_OK = 'Yes, set dates during lifecycle freeze';
const CONFIRM_CANCEL = "No, don't set these dates";
const CONFIRM_EXPLANATION =
    'By default, Runway will not run lifecycle automations (kickoff, submit, release) during lifecycle freezes, and attempts to schedule or reschedule target dates outside of those windows.';

export interface FrozenTargetDate {
    eventType: LifecycleEventType;
    label: string;
    date: dayjs.Dayjs;
    freeze: LifecycleFreezeSet;
}

export interface TargetDatesToCheck {
    targetKickoffDate?: dayjs.Dayjs | null;
    targetSubmitDate?: dayjs.Dayjs | null;
    targetReleaseDate?: dayjs.Dayjs | null;
}

const EVENT_LABELS: Record<string, string> = {
    [LifecycleEventType.LifecycleEventTypeKickoff]: 'kickoff',
    [LifecycleEventType.LifecycleEventTypeSubmit]: 'submit',
    [LifecycleEventType.LifecycleEventTypeRelease]: 'release',
};

const formatFreezeWindow = (freeze: LifecycleFreezeSet): string =>
    `${dayjs(freeze.startDate).format('MMM D')} – ${dayjs(freeze.endDate).format('MMM D, YYYY')}`;

export const useLifecycleFreezeTargetDateWarning = (appId: string) => {
    const lifecycleFreezes = useSelector((state: RootState) => selectLifecycleFreezes(state, appId));
    // Static Modal.confirm renders outside the React tree and misses our ConfigProvider theme
    const [modal, modalContextHolder] = Modal.useModal();

    const frozenTargetDateFor = React.useCallback(
        (date: dayjs.Dayjs | null | undefined, eventType: LifecycleEventType): FrozenTargetDate | undefined => {
            if (!date) {
                return undefined;
            }
            const freeze = findLifecycleFreezeForDate(date, lifecycleFreezes, eventType);
            if (!freeze) {
                return undefined;
            }
            return { eventType, label: EVENT_LABELS[eventType] ?? eventType, date, freeze };
        },
        [lifecycleFreezes],
    );

    const getFrozenTargetDates = React.useCallback(
        (dates: TargetDatesToCheck): FrozenTargetDate[] =>
            [
                frozenTargetDateFor(dates.targetKickoffDate, LifecycleEventType.LifecycleEventTypeKickoff),
                frozenTargetDateFor(dates.targetSubmitDate, LifecycleEventType.LifecycleEventTypeSubmit),
                frozenTargetDateFor(dates.targetReleaseDate, LifecycleEventType.LifecycleEventTypeRelease),
            ].filter((frozen): frozen is FrozenTargetDate => !!frozen),
        [frozenTargetDateFor],
    );

    const confirmTargetDatesDuringFreeze = React.useCallback(
        (dates: TargetDatesToCheck, onConfirm: (allowTargetDatesDuringFreeze: boolean) => void) => {
            const frozen = getFrozenTargetDates(dates);
            if (frozen.length === 0) {
                onConfirm(false);
                return;
            }

            modal.confirm({
                title: CONFIRM_TITLE,
                okText: CONFIRM_OK,
                cancelText: CONFIRM_CANCEL,
                width: 640,
                styles: { body: { padding: 10 } },
                // Overriding a safety default rather than destroying anything, but it is not the
                // happy path either, so it gets danger styling instead of the primary purple.
                okButtonProps: { danger: true },
                content: (
                    <div>
                        <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
                            {frozen.map((frozenTarget) => (
                                <li key={frozenTarget.eventType}>
                                    {`Target ${frozenTarget.label} falls inside the ${formatFreezeWindow(frozenTarget.freeze)} lifecycle freeze.`}
                                </li>
                            ))}
                        </ul>
                        <span>{CONFIRM_EXPLANATION}</span>
                    </div>
                ),
                onOk: () => onConfirm(true),
            });
        },
        [getFrozenTargetDates, modal],
    );

    return {
        modalContextHolder,
        frozenTargetDateFor,
        getFrozenTargetDates,
        confirmTargetDatesDuringFreeze,
        warningForEvent,
    };
};
